<template>
    <ValidationForm :onSubmit="submit">
        <div class="form-group">
            <label for="txtName">Name</label>
            <input type="text" class="form-control" id="txtName" placeholder="Name of the event" v-model="event.name" />
        </div>
        <div class="form-group" v-if="event.event_type_id === 3">
            <label for="txtDescription">Description</label>
            <input type="text" class="form-control" id="txtDescription" placeholder="A friendly description of the event"
                v-model="event.description" />
            <small class="form-text text-muted">This will also be used to describe a notification to a mobile user.</small>
        </div>
        <div class="form-group" v-if="event.event_type_id === 3">
            <label for="ddlDevice">Device</label>
            <select class="form-control" id="ddlDevice" v-model="event.device_id">
                <option value="null">None</option>
                <option v-for="device in filtered_devices" :key="device.device_id" v-bind:value="device.device_id">
                    {{ device.name }}
                </option>
            </select>
            <small class="form-text text-muted">Only relevant if you have a Barionet device.</small>
        </div>
        <div class="form-group" v-if="event.event_type_id === 3 && event.device_id !== null">
            <label for="txtInputNumber">Input</label>
            <input type="text" class="form-control" id="txtInputNumber" v-model="event.input_number" />
        </div>
        <div class="form-group" v-if="event.event_type_id === 3">
            <label for="ddlAlertActionType">Action Type</label>
            <select class="form-control" id="ddlAlertActionType" v-model="event.event_action_type_id">
                <option value="0">Select..</option>
                <option v-for="event_action_type in event_action_types" :key="event_action_type.event_action_type_id"
                    v-bind:value="event_action_type.event_action_type_id">
                    {{ event_action_type.name }}
                </option>
            </select>
        </div>

        <ValidationField id="txtExtension" label="Extension"
            helperText="Not required, but can only play from web interface if left at 9. If inputted, must keep leading 9 and follow with 3 numbers. Example: 9001"
            v-model="event.extension" :validator="validate" />

        <div class="form-check" v-if="event.event_type_id === 2 && !from_template">
            <input id="chkSound" type="checkbox" class="form-check-input" v-model="create_sound_step" />
            <label for="chkSound" class="form-check-label">Play sound for this announcement?</label>
        </div>

        <div class="form-check" v-if="event.event_type_id === 3">
            <input id="chkChat" type="checkbox" class="form-check-input" v-model="event.chat_enabled" @click="handleChatChange"/>
            <label for="chkChat" class="form-check-label">Enable aChat</label>
            <small class="form-text text-muted d-inline ml-2" v-if="!feature_g2_secure_enabled">G2 Secure is not enabled for this account</small>
        </div>

        <div class="form-group" v-if="event.event_type_id === 2 && create_sound_step && !from_template">
            <label for="ddlZone">Zone</label>
            <select class="form-control" id="ddlZone" v-model="zone_id">
                <option value="0">Select..</option>
                <option v-for="zone in zones_for_drop_down" :key="zone.value" :value="zone.value">{{ zone.label }}</option>
            </select>
        </div>

        <div v-if="event.event_type_id === 2 && sound_type_id === 1 && create_sound_step && !from_template">
            <SoundDropDownAndPreview tag="announcement" v-model="sound"></SoundDropDownAndPreview>
        </div>

        <br />

        <button type="submit" class="btn btn-hub-primary">
            Submit
        </button>
        <router-link :to="{ name: event.event_type_id === 3 ? 'AlertIndex' : 'Notifications' }" class="btn btn-secondary">
            Cancel
        </router-link>
    </ValidationForm>
</template>

<script>
import { mapGetters } from 'vuex'
import SoundDropDownAndPreview from '../SoundDropDownAndPreview'
import ValidationMixin from '../../mixins/ValidationMixin.vue'
import { AlertExtensionValidator } from '../../assets/js/validators'
import { DEVICE_TYPE, EVENT_TYPE } from '../../assets/js/resource-types'

export default {
    props: {
        event: Object,
        callback: Function,
        from_template: Boolean
    },
    components: { SoundDropDownAndPreview },
    data() {
        return {
            create_sound_step: true,
            zone_id: 0,
            sound_type_id: 1,
            sound: null,
        }
    },
    mixins: [ValidationMixin],
    computed: {
        ...mapGetters({
            devices: 'allDevices',
            event_action_types: 'allEventActionTypes',
            sound_types: 'allSoundTypes',
            playlists: 'allPlaylists',
            zones_for_drop_down: 'allZonesForDropdown',
            scoped_site_id: 'scopedSiteId',
            feature_g2_secure_enabled: 'featureG2SecureEnabled'
        }),
        filtered_devices() {
            return this.devices.filter(device =>
                device.device_type_id === DEVICE_TYPE.Barionet && device.site_id === this.scoped_site_id
            )
        },
        filtered_playlists: function () {
            return this.playlists.filter(playlist => playlist.site_id === this.scoped_site_id)
        }
    },
    methods: {
        validate(value) {
            const validator = new AlertExtensionValidator(this.extensions_in_use)
            return validator.validate(value)
        },
        handleChatChange(e) {
            if(!this.feature_g2_secure_enabled && e.target.checked) {
                alert('G2 Secure is not enabled for your account. You must have G2 Secure enabled to use aChat for an alert. For more information, please visit our website at https://www.galaxynext.us/, or contact us at sales@galaxynext.us to get started.')
                e.target.checked = false
            }
        },
        submit() {
            if(!this.from_template && this.event.event_type_id === EVENT_TYPE.Announcement && this.create_sound_step && (Number(this.zone_id) === 0 || this.sound === null)) {
                alert('Select a zone and sound')
                return
            }
            const next = this.from_template || this.event.event_type_id === EVENT_TYPE.Announcement && this.create_sound_step ? null : 'EventSummary'
            this.$store.dispatch('addEditEvent', { event: this.event, next })
                .then(event => {
                    if(this.callback) {
                        if(event.event_type_id === EVENT_TYPE.Announcement && this.create_sound_step) {
                            this.callback(event.event_id, this.zone_id, this.sound_type_id, this.sound)
                        } else if(this.from_template) {
                            this.callback(event.event_id)
                        }
                    }
                })
        }
    },
    created() {
        this.$store.dispatch('getEventActionTypes')

        if (this.event.event_type_id === EVENT_TYPE.Alert) {
            this.$store.dispatch('getDevices')
        }

        if (this.event.event_type_id === EVENT_TYPE.Announcement) {
            this.$store.dispatch('getZones')
            this.$store.dispatch('getSoundTypes')
            this.$store.dispatch('getPlaylists')
        }
    },
    mounted() {
        if(this.feature_g2_secure_enabled && this.event.event_type_id === EVENT_TYPE.Alert) {
            this.event.chat_enabled = true
        }
    }
}
</script>